import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DiscountRateInListDto, DiscountRatesApi, EditDiscountRateDto } from '@buspro/web/apis';
import { ErrorDisplayComponent, ExtendedProblemDetail, useFormErrorHandler } from '@buspro/web/core';
import { DigitOnlyModule } from '@uiowa/digit-only';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzInputDirective, NzInputGroupComponent } from 'ng-zorro-antd/input';
import { NzModalComponent, NzModalContentDirective } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';

@Component({
  selector: 'tenant-edit-discount-rate',
  templateUrl: './edit-discount-rate.component.html',
  styleUrls: ['./edit-discount-rate.component.css'],
  imports: [
    CommonModule,
    NzModalComponent,
    NzModalContentDirective,
    ReactiveFormsModule,
    NzFormDirective,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzFormControlComponent,
    NzInputDirective,
    NzInputGroupComponent,
    NzCheckboxModule,
    ErrorDisplayComponent,
    DigitOnlyModule,
  ],
  standalone: true,
})
export class EditDiscountRateComponent {
  private formBuilder = inject(FormBuilder);

  form = this.formBuilder.group({
    name: ['', [Validators.required, Validators.maxLength(100)]],
    description: [''],
    code: ['', [Validators.pattern(/^[A-Z0-9_-]*$/), Validators.maxLength(20)]],
    rate: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
    applicableToBookings: [true],
    applicableToParcels: [false],
  });
  @Input()
  show = false;
  @Output()
  hide = new EventEmitter(true);

  // Add isSubmitting flag
  isSubmitting = false;

  // Standardized error handling
  private readonly formErrorHandler = useFormErrorHandler();
  showError = signal(false);
  errorDetails: ExtendedProblemDetail | null = null;

  constructor(
    private discountRatesApi: DiscountRatesApi,
    private notification: NzNotificationService,
  ) {}

  private _rate!: DiscountRateInListDto;

  get rate(): DiscountRateInListDto {
    return this._rate;
  }

  @Input()
  set rate(value: DiscountRateInListDto) {
    if (value) {
      this._rate = value;
      this.form.patchValue({
        name: value.name,
        description: value.description || '',
        code: value.code || '',
        rate: value.rate,
        applicableToBookings: value.applicableToBookings,
        applicableToParcels: value.applicableToParcels,
      });
    }
  }

  closeDrawer() {
    if (this.isSubmitting) return;

    this.hide.emit(true);
    this.show = false;
  }

  private clearErrors() {
    this.showError.set(false);
    this.errorDetails = null;
  }

  onDismissError() {
    this.clearErrors();
  }

  saveChanges() {
    if (this.isSubmitting) return;

    this.clearErrors();

    const payload: EditDiscountRateDto = {
      id: this.rate.id,
      name: this.form.get('name')!.value!,
      description: this.form.get('description')!.value || undefined,
      code: this.form.get('code')!.value?.toUpperCase() || undefined,
      rate: +this.form.get('rate')!.value!,
      applicableToBookings: this.form.get('applicableToBookings')!.value ?? true,
      applicableToParcels: this.form.get('applicableToParcels')!.value ?? false,
    };

    this.isSubmitting = true;

    this.discountRatesApi.discountRatesControllerUpdate({ id: payload.id, body: payload }).subscribe(
      () => {
        this.show = false;
        this.closeDrawer();
        this.notification.success('✅ Success', 'Discount rate has been updated successfully');
        this.isSubmitting = false;
      },
      (err) => {
        const result = this.formErrorHandler.handleFormError(err, { formName: 'tenant-edit-discount-rate-form' });
        this.errorDetails = result.problemDetail ?? null;
        this.showError.set(!!this.errorDetails);
        this.isSubmitting = false;
      },
    );
  }
}
